import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ReactMarkdown from 'react-markdown';
import { useReggie } from '../context/ReggieContext';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, Check, Zap, Copy, Bookmark, RefreshCw,
  Loader2, Sparkles, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { HelpPanel } from '../components/HelpPanel';
import { ExportDropdown } from '../components/ExportDropdown';
import { UpgradeModal } from '../components/UpgradeModal';
import { ErrorBanner } from '../components/ErrorBanner';
import { CelebrationModal } from '../components/CelebrationModal';
import { Confetti } from '../components/Confetti';
import { SegmentedProgress } from '../components/SegmentedProgress';
import { StepReferencePanels } from '../components/StepReferencePanels';
import { getStepMeta, getPhase, getMotivationalTagline } from '../lib/stepIcons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Variable input configurations per prompt key (v2 keys)
const PROMPT_VARIABLES = {
  // ========== FAST CASH PATH ==========
  fast_cash_offer_generator: [
    { key: 'skills', label: 'Your skills or things you\'re good at', placeholder: 'e.g., writing, graphic design, social media, organizing' },
    { key: 'experience_level', label: 'Your experience level', placeholder: 'e.g., Complete beginner, Some experience, Experienced' },
    { key: 'available_hours', label: 'Hours you can work per week', placeholder: 'e.g., 5-10 hours, 10-20 hours, 20+ hours' }
  ],
  fast_cash_offer_page_copy: [
    { key: 'service_name', label: 'Your offer name', placeholder: 'e.g., Social Media Starter Package' },
    { key: 'target_client', label: 'Who is it for?', placeholder: 'e.g., Small business owners who need social media help' },
    { key: 'main_benefit', label: 'The main result they get', placeholder: 'e.g., Consistent social media posts without the hassle' },
    { key: 'deliverables', label: 'What you deliver', placeholder: 'e.g., 12 posts/month, content calendar, hashtag research' }
  ],
  fast_cash_pricing_helper: [
    { key: 'service_name', label: 'Service name', placeholder: 'e.g., Website Audit Package' },
    { key: 'deliverables', label: 'What you deliver', placeholder: 'e.g., Full audit report, 30-min call, action plan' },
    { key: 'time_per_delivery', label: 'How long it takes to deliver', placeholder: 'e.g., 2-3 hours of my time' },
    { key: 'competitor_range', label: 'What competitors charge (if you know)', placeholder: 'e.g., $100-$500, or "not sure"' }
  ],
  fast_cash_outreach_script: [
    { key: 'service_name', label: 'Service name', placeholder: 'e.g., Email Marketing Setup' },
    { key: 'target_audience', label: 'Who you\'re reaching out to', placeholder: 'e.g., Online course creators' },
    { key: 'platform', label: 'Platform you\'ll use', placeholder: 'e.g., LinkedIn, Instagram DM, Email' },
    { key: 'main_result', label: 'The main result you help them get', placeholder: 'e.g., Automated email sequences that sell while they sleep' }
  ],
  fast_cash_followup_script: [
    { key: 'service_name', label: 'Service name', placeholder: 'e.g., Social Media Management' },
    { key: 'target_audience', label: 'Who you reached out to', placeholder: 'e.g., Local restaurant owners' },
    { key: 'platform', label: 'Platform', placeholder: 'e.g., Instagram DM, Email, LinkedIn' },
    { key: 'initial_response', label: 'How they responded (or didn\'t)', placeholder: 'e.g., No reply yet, Said "sounds interesting", Asked about price' }
  ],

  // ========== DIGITAL PRODUCT PATH ==========
  digital_product_idea_generator: [
    { key: 'expertise', label: 'What you know about or are good at', placeholder: 'e.g., fitness, cooking, productivity, parenting' },
    { key: 'audience_type', label: 'The kind of people you want to help', placeholder: 'e.g., Busy moms, college students, small business owners' },
    { key: 'preferred_format', label: 'Format you prefer', placeholder: 'e.g., Ebook, template pack, mini-course, checklist' }
  ],
  digital_product_transformation_builder: [
    { key: 'product_topic', label: 'Product topic', placeholder: 'e.g., Meal prep for busy professionals' },
    { key: 'target_buyer', label: 'Who will buy it', placeholder: 'e.g., Working parents who want to eat healthier' },
    { key: 'product_format', label: 'Product format', placeholder: 'e.g., Ebook, Template, Mini-course' }
  ],
  digital_product_outline_generator: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., The 30-Day Meal Prep Guide' },
    { key: 'product_format', label: 'Format', placeholder: 'e.g., Ebook, Video course, Template pack' },
    { key: 'transformation', label: 'Core transformation', placeholder: 'e.g., Go from takeout every night to easy home-cooked meals' },
    { key: 'num_sections', label: 'Target length', placeholder: 'e.g., 5 chapters, 8 modules, 10 templates' }
  ],
  digital_product_funnel_copy: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., The Meal Prep Mastery Guide' },
    { key: 'price', label: 'Price', placeholder: 'e.g., $27' },
    { key: 'transformation', label: 'Core transformation', placeholder: 'e.g., Save 10+ hours per week on meal planning' },
    { key: 'target_buyer', label: 'Who it\'s for', placeholder: 'e.g., Busy professionals who hate cooking' }
  ],
  digital_product_launch_posts: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., Notion Productivity Templates' },
    { key: 'main_benefit', label: 'Main benefit', placeholder: 'e.g., Organize your entire life in one dashboard' },
    { key: 'price', label: 'Price', placeholder: 'e.g., $19' },
    { key: 'platforms', label: 'Platform(s) to post on', placeholder: 'e.g., Instagram, Twitter, LinkedIn' }
  ],

  // ========== FACELESS AFFILIATE PATH ==========
  affiliate_offer_angle_selector: [
    { key: 'niche_interest', label: 'Your niche interest', placeholder: 'e.g., personal finance, fitness, tech gadgets, self-improvement' },
    { key: 'content_style', label: 'Content style you prefer', placeholder: 'e.g., Educational tips, Listicles, Reviews, Stories' },
    { key: 'audience_type', label: 'The audience you want to reach', placeholder: 'e.g., College students, side-hustlers, stay-at-home parents' }
  ],
  affiliate_bridge_page_copy: [
    { key: 'offer_name', label: 'Affiliate offer name', placeholder: 'e.g., ConvertKit email marketing' },
    { key: 'target_audience', label: 'Who you\'re targeting', placeholder: 'e.g., Content creators starting their email list' },
    { key: 'main_benefit', label: 'Main benefit of the offer', placeholder: 'e.g., Easy email automation for creators' },
    { key: 'your_angle', label: 'Your unique angle', placeholder: 'e.g., The tool I recommend for growing a list from zero' }
  ],
  affiliate_content_generator: [
    { key: 'offer_name', label: 'Affiliate offer', placeholder: 'e.g., Canva Pro' },
    { key: 'platform', label: 'Platform', placeholder: 'e.g., Instagram, TikTok, Pinterest' },
    { key: 'content_angle', label: 'Your content angle', placeholder: 'e.g., Design tips for non-designers' },
    { key: 'audience_pain_point', label: 'Main pain point of your audience', placeholder: 'e.g., They spend hours making ugly graphics' }
  ],
  affiliate_cta_generator: [
    { key: 'offer_name', label: 'Affiliate offer', placeholder: 'e.g., Skillshare Premium' },
    { key: 'platform', label: 'Main platform', placeholder: 'e.g., Instagram, YouTube, TikTok' },
    { key: 'content_type', label: 'Type of content you make', placeholder: 'e.g., Carousels, Reels, Text posts' },
    { key: 'link_placement', label: 'Where you put your link', placeholder: 'e.g., Bio only, Link in comments, Swipe up' }
  ],
  affiliate_tracking_notes_generator: [
    { key: 'offer_name', label: 'Affiliate offer', placeholder: 'e.g., Bluehost web hosting' },
    { key: 'platforms_used', label: 'Platforms you post on', placeholder: 'e.g., Instagram, Pinterest, TikTok' },
    { key: 'content_types', label: 'Types of content you create', placeholder: 'e.g., Carousels, text posts, quote graphics' },
    { key: 'goal', label: 'Your main goal right now', placeholder: 'e.g., Get my first 10 affiliate clicks, Make first sale' }
  ],

  // ========== FUNNEL BUILDER (ALL PATHS) ==========
  funnel_headline_generator: [
    { key: 'offer_name', label: 'What you\'re selling', placeholder: 'e.g., Social Media Starter Package, Meal Prep Guide' },
    { key: 'target_audience', label: 'Who it\'s for', placeholder: 'e.g., Busy professionals, small business owners' },
    { key: 'main_result', label: 'The main result they get', placeholder: 'e.g., Save 10 hours/week, get their first 3 clients' },
    { key: 'pain_point', label: 'Their biggest frustration', placeholder: 'e.g., Wasting time on social media with no results' }
  ],
  funnel_offer_builder: [
    { key: 'offer_name', label: 'Offer name', placeholder: 'e.g., The Complete Social Media Setup' },
    { key: 'deliverables', label: 'What they get', placeholder: 'e.g., 30 posts, content calendar, strategy call' },
    { key: 'price', label: 'Price', placeholder: 'e.g., $97, $27/month' },
    { key: 'ideal_buyer', label: 'Who it\'s perfect for', placeholder: 'e.g., New business owners who hate social media' },
    { key: 'transformation', label: 'The transformation', placeholder: 'e.g., Go from invisible to posting consistently' }
  ],
  funnel_content_builder: [
    { key: 'offer_name', label: 'Offer name', placeholder: 'e.g., Freelance Starter Kit' },
    { key: 'target_audience', label: 'Target audience', placeholder: 'e.g., People who want to freelance but don\'t know where to start' },
    { key: 'problem', label: 'Their main problem', placeholder: 'e.g., They know they have skills but can\'t get clients' },
    { key: 'solution', label: 'Your solution', placeholder: 'e.g., A step-by-step system to land your first 3 clients' },
    { key: 'proof_points', label: 'Results or proof', placeholder: 'e.g., Helped 50+ people land clients, tested method, money-back guarantee' }
  ],
  funnel_cta_generator: [
    { key: 'offer_name', label: 'Offer name', placeholder: 'e.g., Website Audit Package' },
    { key: 'price', label: 'Price', placeholder: 'e.g., $47' },
    { key: 'main_benefit', label: 'Main benefit', placeholder: 'e.g., Know exactly what\'s wrong with your site in 24 hours' },
    { key: 'next_step', label: 'What happens after they click', placeholder: 'e.g., They fill out a quick form, They get instant access' }
  ],
  funnel_tech_setup: [
    { key: 'offer_name', label: 'What you\'re selling', placeholder: 'e.g., Freelance Outreach Templates' },
    { key: 'budget', label: 'Budget for tools', placeholder: 'e.g., Free only, Under $50/month, Flexible' },
    { key: 'tech_level', label: 'Your tech comfort level', placeholder: 'e.g., Total beginner, Can figure things out, Comfortable' },
    { key: 'payment_method', label: 'How you\'ll collect payment', placeholder: 'e.g., Stripe, PayPal, Gumroad, Not sure yet' }
  ],
  funnel_launch_guide: [
    { key: 'offer_name', label: 'What you\'re launching', placeholder: 'e.g., Social Media Management Package' },
    { key: 'target_audience', label: 'Target audience', placeholder: 'e.g., Local business owners' },
    { key: 'platforms', label: 'Platforms you\'re on', placeholder: 'e.g., Instagram, LinkedIn, Facebook, X, TikTok, YouTube' },
    { key: 'audience_size', label: 'Current audience size', placeholder: 'e.g., 0 followers, 200 connections, 500 email subscribers' }
  ],

  // ========== PRODUCT CREATION (DIGITAL PRODUCT PATH) ==========
  product_type_selector: [
    { key: 'product_topic', label: 'Product topic', placeholder: 'e.g., Budget meal planning, Productivity systems' },
    { key: 'skills', label: 'Your skills', placeholder: 'e.g., Writing, Design, Video, Teaching' },
    { key: 'time_available', label: 'Time to create it', placeholder: 'e.g., One weekend, A week, 2-3 weeks' },
    { key: 'audience_preference', label: 'What your audience prefers', placeholder: 'e.g., Quick reads, Visual guides, Video walkthroughs' }
  ],
  product_outcome_definer: [
    { key: 'product_topic', label: 'Product topic', placeholder: 'e.g., Email marketing for beginners' },
    { key: 'product_format', label: 'Format', placeholder: 'e.g., Ebook, Template pack, Mini-course' },
    { key: 'target_buyer', label: 'Target buyer', placeholder: 'e.g., New online business owners' },
    { key: 'buyer_struggle', label: 'What they struggle with', placeholder: 'e.g., They don\'t know what to write in emails' }
  ],
  product_content_generator: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., The Email Starter Kit' },
    { key: 'section_topic', label: 'This section\'s topic', placeholder: 'e.g., Writing your welcome email' },
    { key: 'section_outcome', label: 'What the reader can DO after', placeholder: 'e.g., Write and send their first welcome email' },
    { key: 'skill_level', label: 'Your buyer\'s experience level', placeholder: 'e.g., Complete beginner, Has some knowledge, Experienced' }
  ],
  product_packaging_guide: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., The Freelancer\'s Toolkit' },
    { key: 'product_format', label: 'Format', placeholder: 'e.g., PDF + templates, Video course, Notion workspace' },
    { key: 'num_items', label: 'Number of sections/items', placeholder: 'e.g., 5 chapters, 10 templates, 8 videos' },
    { key: 'price', label: 'Price point', placeholder: 'e.g., $19, $47, $97' }
  ],
  product_idea_generator: [
    { key: 'problems_solved', label: 'Problems you\'ve solved for yourself', placeholder: 'e.g., Figured out meal prep, Organized my finances, Built a side income' },
    { key: 'questions_asked', label: 'Questions people ask you', placeholder: 'e.g., How do you stay organized? How did you learn to cook?' },
    { key: 'research_topics', label: 'Things you\'ve spent hours researching', placeholder: 'e.g., Productivity systems, Home workouts, Budget travel' },
    { key: 'time_available', label: 'Time to create', placeholder: 'e.g., A weekend, One week, A few hours' }
  ],
  product_outline_builder: [
    { key: 'product_name', label: 'Product name', placeholder: 'e.g., The Budget Travel Playbook' },
    { key: 'core_outcome', label: 'Core outcome', placeholder: 'e.g., Plan a full trip for under $1000' },
    { key: 'target_buyer', label: 'Target buyer', placeholder: 'e.g., First-time travelers on a budget' },
    { key: 'desired_length', label: 'Desired length', placeholder: 'e.g., Short (5 sections), Medium (8-10), Detailed (15+)' }
  ]
};

const StepExecution = () => {
  const { pathId, stepNumber } = useParams();
  const { user, profile, currentPath, session, refreshProfile, getAuthHeaders } = useAuth();
  const { updateContext } = useReggie();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(null);
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState(null);
  const [allProgress, setAllProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // AI Generation state
  const [variables, setVariables] = useState({});
  const [autoFilledKeys, setAutoFilledKeys] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [outputId, setOutputId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitSubscription, setLimitSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completePulse, setCompletePulse] = useState(false);
  const [allOutputs, setAllOutputs] = useState([]);
  const [parsedOptions, setParsedOptions] = useState([]);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
  const [extractingOption, setExtractingOption] = useState(false);

  const stepNum = parseInt(stepNumber);

  useEffect(() => {
    let stepsData = null;
    const fetchData = async () => {
      setError(null);
      try {
        const authHeaders = getAuthHeaders();
        const [stepsRes, progressRes, outputsRes] = await Promise.all([
          fetch(`${API}/paths/${pathId}/steps`),
          fetch(`${API}/progress/me/${pathId}`, { headers: authHeaders }),
          fetch(`${API}/outputs/me`, { headers: authHeaders })
        ]);

        if (progressRes.status === 401 || outputsRes.status === 401) {
          setError({ message: 'Your session has expired. Please sign in again.', isAuth: true });
          return;
        }

        if (!stepsRes.ok) {
          setError({ message: 'Failed to load step data. The server may be temporarily unavailable.' });
          return;
        }

        let fetchedOutputs = [];
        if (outputsRes.ok) {
          fetchedOutputs = await outputsRes.json();
          setAllOutputs(fetchedOutputs);
        }

        if (stepsRes.ok) {
          stepsData = await stepsRes.json();
          setSteps(stepsData);
          const currentStep = stepsData.find(s => s.step_number === stepNum);
          setStep(currentStep);
          
          if (currentStep?.ai_prompt_key) {
            const config = PROMPT_VARIABLES[currentStep.ai_prompt_key] || [];

            // Build a map of all previously entered variables from earlier steps
            const previousVars = {};
            stepsData.forEach(s => {
              if (s.step_number < stepNum) {
                const output = fetchedOutputs.find(o => o.step_id === s.id);
                if (output?.input_variables) {
                  Object.entries(output.input_variables).forEach(([k, v]) => {
                    if (v && typeof v === 'string' && v.trim()) {
                      previousVars[k] = v;
                    }
                  });
                }
              }
            });

            // Pre-fill: use previous values for matching keys, empty for new ones
            const initialVars = {};
            const autoFilled = new Set();
            config.forEach(v => {
              if (previousVars[v.key]) {
                initialVars[v.key] = previousVars[v.key];
                autoFilled.add(v.key);
              } else {
                initialVars[v.key] = '';
              }
            });
            setVariables(initialVars);
            setAutoFilledKeys(autoFilled);
          }

          // Find existing output for this step
          if (currentStep) {
            const stepOutput = fetchedOutputs.find(o => o.step_id === currentStep.id);
            if (stepOutput) {
              setGeneratedContent(stepOutput.generated_content);
              setOutputId(stepOutput.id);
              setIsSaved(stepOutput.is_saved);
            }
          }
        }

        if (progressRes.ok) {
          const progressData = await progressRes.json();
          setAllProgress(progressData);
          if (stepsData) {
            const currentStep = stepsData.find(s => s.step_number === stepNum);
            const stepProg = progressData.find(p => p.step_id === currentStep?.id);
            setProgress(stepProg);
          }
        }
      } catch (err) {
        process.env.NODE_ENV === "development" && console.error('Error fetching step data:', err);
        setError({ message: 'Could not connect to the server. Check your connection and try again.' });
      } finally {
        setLoading(false);
      }
    };

    // Reset state on step change
    setGeneratedContent('');
    setOutputId(null);
    setIsSaved(false);
    setLoading(true);
    fetchData();
  }, [pathId, stepNum, user]);

  // Update Reggie context when step changes
  useEffect(() => {
    if (step) {
      const promptConfig = PROMPT_VARIABLES[step.ai_prompt_key];
      const currentQuestion = promptConfig
        ? promptConfig.map(v => v.label).join(', ')
        : step.action_required || '';
      updateContext({
        path_name: step.path_id?.replace('path_', '').replace(/_/g, ' '),
        step_title: step.title,
        step_description: step.description,
        current_question: currentQuestion
      });
    }
  }, [step, updateContext]);

  const handleVariableChange = (key, value) => {
    setVariables(prev => ({ ...prev, [key]: value }));
  };

  // Listen for Reggie "Use This" events
  useEffect(() => {
    const handler = (e) => {
      const { answer } = e.detail;
      if (answer && step?.ai_prompt_key) {
        const promptConfig = PROMPT_VARIABLES[step.ai_prompt_key];
        if (promptConfig && promptConfig.length > 0) {
          // Insert into the first empty field, or first field if all filled
          const firstEmpty = promptConfig.find(v => !variables[v.key]);
          const target = firstEmpty || promptConfig[0];
          setVariables(prev => ({ ...prev, [target.key]: answer }));
        }
      }
    };
    window.addEventListener('reggie-use-this', handler);
    return () => window.removeEventListener('reggie-use-this', handler);
  }, [step, variables]);

  const handleGenerate = async () => {
    if (!step?.ai_prompt_key) return;

    setGenerating(true);
    try {
      const token = session?.access_token;
      const response = await fetch(`${API}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          prompt_key: step.ai_prompt_key,
          user_path_id: currentPath?.id,
          step_id: step.id,
          input_variables: variables
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const detail = err.detail;

        // Generation limit hit (429 or detail.type)
        if (response.status === 429 || detail?.type === 'generation_limit') {
          setLimitSubscription({
            generations_used: detail?.generations_used ?? 0,
            monthly_generations: detail?.monthly_generations ?? 2,
            plan_id: detail?.plan_id ?? 'free',
          });
          setShowUpgradeModal(true);
          return;
        }

        const msg = typeof detail === 'string' ? detail : detail?.message || 'Generation failed';
        throw new Error(msg);
      }

      const data = await response.json();
      setGeneratedContent(data.generated_content);
      setOutputId(data.id);
      setIsSaved(false);
      toast.success('Here\u2019s your guide!');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Generation error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    toast.success('Copied to clipboard!');
  };

  // Parse options from generated markdown content
  const parseOptions = (content) => {
    if (!content) return [];
    const options = [];
    // Match patterns like "## Option 1:", "## 1.", "## Option A:", or numbered headers
    const lines = content.split('\n');
    let currentOption = null;

    for (const line of lines) {
      const optionMatch = line.match(/^##\s+(Option\s+\d+|Choice\s+\d+|\d+\.)\s*[:\-–]\s*(.+)/i)
        || line.match(/^##\s+(My\s+(?:#?\d+\s+)?Recommendation|Top\s+Pick|Best\s+(?:Option|Choice))\s*[:\-–]?\s*(.*)/i);
      if (optionMatch) {
        if (currentOption) options.push(currentOption);
        currentOption = { title: line.replace(/^##\s+/, '').trim(), body: '', raw: line + '\n' };
      } else if (currentOption) {
        currentOption.body += line + '\n';
        currentOption.raw += line + '\n';
      }
    }
    if (currentOption) options.push(currentOption);
    // Only return if we found 2+ options
    return options.length >= 2 ? options : [];
  };

  // When content is generated, try to parse options
  useEffect(() => {
    const options = parseOptions(generatedContent);
    setParsedOptions(options);
    setSelectedOptionIdx(null);
  }, [generatedContent]);

  const handleSelectOption = async (idx) => {
    setSelectedOptionIdx(idx);
    setExtractingOption(true);
    const option = parsedOptions[idx];

    try {
      const token = session?.access_token;
      if (!token || !step?.ai_prompt_key) return;

      const config = PROMPT_VARIABLES[step.ai_prompt_key] || [];
      if (config.length === 0) return;

      const response = await fetch(`${API}/extract-option-values`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          option_text: option.raw,
          field_configs: config.map(c => ({ key: c.key, label: c.label }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.values) {
          setVariables(prev => {
            const updated = { ...prev };
            Object.entries(data.values).forEach(([k, v]) => {
              if (v && config.some(c => c.key === k)) {
                updated[k] = v;
              }
            });
            return updated;
          });
          toast.success('Fields updated with your selection!');
        }
      }
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Option extract failed:', err);
    } finally {
      setExtractingOption(false);
    }
  };

  const handleSave = async () => {
    if (!outputId) return;
    try {
      const response = await fetch(`${API}/outputs/${outputId}/save`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data.is_saved);
        toast.success(data.is_saved ? 'Saved to workspace!' : 'Removed from workspace');
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleMarkComplete = async () => {
    try {
      if (!progress && currentPath) {
        await fetch(`${API}/progress`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            user_path_id: currentPath.id,
            step_id: step.id
          })
        });
      }

      await fetch(`${API}/progress/me/${step.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_completed: true })
      });

      await refreshProfile();
      toast.success('Step completed!');

      // Trigger confetti + icon pulse before navigating
      setShowConfetti(true);
      setCompletePulse(true);

      const nextStep = steps.find(s => s.step_number === stepNum + 1);
      if (nextStep) {
        setTimeout(() => {
          navigate(`/path/${pathId}/step/${stepNum + 1}`);
        }, 1500);
      } else {
        // Last step — show celebration after a short beat
        setTimeout(() => setShowCelebration(true), 1100);
      }
    } catch (error) {
      toast.error('Failed to mark as complete');
    }
  };

  const canGenerate = () => {
    if (!step?.ai_prompt_key) return false;
    const config = PROMPT_VARIABLES[step.ai_prompt_key] || [];
    return config.every(v => variables[v.key]?.trim());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <ErrorBanner
          message={error.message}
          isAuthError={error.isAuth}
          onRetry={!error.isAuth ? () => window.location.reload() : undefined}
        />
      </div>
    );
  }

  if (!step) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Step not found</p>
      </div>
    );
  }

  const variableConfig = PROMPT_VARIABLES[step.ai_prompt_key] || [];
  const isCompleted = progress?.is_completed;
  const nextStep = steps.find(s => s.step_number === stepNum + 1);

  return (
    <div className="min-h-screen">
      <Toaster position="top-center" richColors />
      
      {/* Slim header with logo + breadcrumb + step navigation */}
      <header className="border-b border-border/60 sticky top-0 bg-background/95 backdrop-blur-sm z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Link to={`/path/${pathId}`} className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors flex-shrink-0" data-testid="back-to-steps">
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <Link to="/dashboard" className="hidden sm:flex items-center transition-opacity hover:opacity-80 flex-shrink-0">
                <img src="/banner-logo.png" alt="Creataby" className="h-9" />
              </Link>
            </div>

            {/* Breadcrumb */}
            <div className="flex-1 min-w-0 text-center text-xs sm:text-sm text-muted-foreground truncate">
              <span className="font-medium text-foreground">
                {steps[0]?.path_id === 'path_fast_cash' ? 'Fast Cash Path' : steps[0]?.path_id === 'path_digital_product' ? 'Digital Product Path' : steps[0]?.path_id === 'path_faceless_affiliate' ? 'Faceless Affiliate Path' : 'Your Path'}
              </span>
              <span className="mx-1.5 text-muted-foreground">›</span>
              <span>Step {stepNum} of {steps.length}</span>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              <HelpPanel />
              {stepNum > 1 && (
                <Link to={`/path/${pathId}/step/${stepNum - 1}`}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="prev-step-btn">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </Link>
              )}
              {nextStep && (
                <Link to={`/path/${pathId}/step/${stepNum + 1}`}>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="next-step-btn">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Segmented phase progress */}
          <div className="pb-3">
            <SegmentedProgress
              steps={steps}
              progress={allProgress}
              currentStepNum={stepNum}
              pathId={pathId}
              showLabels
            />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 creataby-page-in" style={{ '--accent': getStepMeta(stepNum).accent }}>
        {/* Step Hero Header */}
        {(() => {
          const meta = getStepMeta(stepNum);
          const phase = getPhase(stepNum);
          const HeroIcon = meta.icon;
          return (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
              data-testid="step-hero-header"
            >
              <div
                className="relative rounded-2xl border border-slate-200 px-5 py-5 sm:px-6 sm:py-6 overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${meta.accent}0d 0%, transparent 70%)`,
                }}
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  {/* Large gradient icon square */}
                  <div
                    key={`hero-icon-${stepNum}-${completePulse ? 'pulse' : 'idle'}`}
                    className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_rgba(0,0,0,0.10)] ${
                      completePulse ? 'creataby-icon-pulse creataby-ripple' : 'creataby-icon-entrance'
                    }`}
                    data-testid="step-hero-icon"
                  >
                    {isCompleted && !completePulse ? (
                      <CheckCircle2 className="text-white" size={36} strokeWidth={2.4} />
                    ) : (
                      <HeroIcon className="text-white" size={36} strokeWidth={2.2} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: meta.accent }}
                        data-testid="step-overline"
                      >
                        Step {stepNum} of {steps.length}
                      </p>
                      <span
                        className="hidden sm:inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold flex-shrink-0"
                        style={{
                          background: `${phase.color}26`,
                          color: phase.color,
                        }}
                        data-testid="phase-badge"
                      >
                        <span>{phase.emoji}</span>
                        {phase.label}
                      </span>
                    </div>
                    <h1
                      className="text-2xl sm:text-[28px] font-bold tracking-tight text-foreground leading-tight"
                      data-testid="step-title"
                    >
                      {step.title}
                    </h1>
                    <p className="text-muted-foreground text-[15px] mt-1.5">{step.description}</p>
                  </div>
                </div>

                {/* Confetti burst on completion */}
                {showConfetti && <Confetti />}
              </div>
            </motion.div>
          );
        })()}

        {/* "Here's what to do" → "YOUR MISSION" directive card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <div
            className="bg-white rounded-xl px-4 py-3.5 border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
            style={{ borderLeft: `4px solid ${getStepMeta(stepNum).accent}` }}
            data-testid="mission-card"
          >
            <p
              className="text-[10px] uppercase tracking-widest mb-1 font-bold"
              style={{ color: getStepMeta(stepNum).accent }}
            >
              Your Mission
            </p>
            <p className="text-foreground text-[15px] font-medium leading-relaxed">{step.action_required}</p>
          </div>
        </motion.div>

        {/* Generator Section */}
        {step.has_ai_generator && (
          <>
          {/* Reference panels: prior outputs the current step depends on */}
          <StepReferencePanels
            currentPromptKey={step.ai_prompt_key}
            allOutputs={allOutputs}
            pathId={pathId}
          />

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <Card
              className="border bg-white"
              style={{
                boxShadow: `0 0 0 2px ${getStepMeta(stepNum).accent}33, 0 4px 24px rgba(0,0,0,0.06)`,
                borderColor: 'transparent',
              }}
              data-testid="ai-generator-card"
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4" style={{ color: getStepMeta(stepNum).accent }} />
                  Fill in the details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {variableConfig.map((v) => (
                  <div key={v.key} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={v.key} className="text-sm font-medium text-slate-700">
                        {v.label}
                      </Label>
                      {autoFilledKeys.has(v.key) && variables[v.key] && (
                        <span className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded" data-testid={`autofill-badge-${v.key}`}>
                          from previous step
                        </span>
                      )}
                    </div>
                    <Input
                      id={v.key}
                      placeholder={v.placeholder}
                      value={variables[v.key] || ''}
                      onChange={(e) => handleVariableChange(v.key, e.target.value)}
                      className="border-slate-200 h-10 creataby-accent-input placeholder:italic placeholder:text-slate-400"
                      data-testid={`input-${v.key}`}
                    />
                  </div>
                ))}

                <Button
                  onClick={handleGenerate}
                  disabled={!canGenerate() || generating}
                  className="w-full text-white hover:opacity-95 h-11 creataby-shimmer-idle creataby-btn-pop font-semibold"
                  style={{
                    background: `linear-gradient(135deg, ${getStepMeta(stepNum).accent}, ${getStepMeta(stepNum).accent}cc)`,
                    boxShadow: `0 2px 14px ${getStepMeta(stepNum).accent}40`,
                  }}
                  data-testid="guide-me-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Working on it...
                    </>
                  ) : generatedContent ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Regenerate
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Guide Me
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
          </>
        )}

        {/* Generated Content — Markdown rendered */}
        <AnimatePresence>
          {generatedContent && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6"
              data-testid="generated-content-section"
            >
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-base">Your Result</CardTitle>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={handleCopy} className="h-8 text-xs" data-testid="copy-btn">
                        <Copy className="h-3.5 w-3.5 mr-1" />
                        Copy
                      </Button>
                      <ExportDropdown content={generatedContent} promptKey={step?.ai_prompt_key} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-secondary/50 rounded-xl p-4 sm:p-5 max-h-[500px] overflow-y-auto">
                    <div className="prose prose-sm prose-neutral max-w-none
                      prose-headings:text-foreground prose-headings:font-semibold prose-headings:tracking-tight
                      prose-h2:text-base prose-h2:mt-5 prose-h2:mb-2
                      prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-1.5
                      prose-p:text-foreground/90 prose-p:text-sm prose-p:leading-relaxed prose-p:my-1.5
                      prose-li:text-foreground/90 prose-li:text-sm prose-li:my-0.5
                      prose-strong:text-foreground prose-strong:font-semibold
                      prose-hr:border-border prose-hr:my-4
                      prose-ul:my-2 prose-ol:my-2
                    ">
                      <ReactMarkdown>{generatedContent}</ReactMarkdown>
                    </div>
                  </div>

                  {/* Selectable option cards */}
                  {parsedOptions.length >= 2 && (
                    <div className="mt-4 border-t border-border/50 pt-4" data-testid="option-cards">
                      <p className="text-sm font-medium text-foreground mb-2.5">Select an option to auto-fill your fields:</p>
                      <div className="space-y-2">
                        {parsedOptions.map((opt, idx) => (
                          <button
                            key={`option-${idx}-${opt.title.slice(0,20)}`}
                            onClick={() => handleSelectOption(idx)}
                            disabled={extractingOption}
                            className={`w-full text-left p-3 rounded-lg border transition-all duration-150 ${
                              selectedOptionIdx === idx
                                ? 'border-teal-500 bg-teal-700/10 ring-1 ring-teal-500/30'
                                : 'border-border hover:border-teal-500/40 hover:bg-secondary/50'
                            }`}
                            data-testid={`option-card-${idx}`}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                selectedOptionIdx === idx ? 'border-teal-500 bg-teal-700' : 'border-muted-foreground/40'
                              }`}>
                                {selectedOptionIdx === idx && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground">{opt.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                  {opt.body.replace(/[#*\-]/g, '').trim().slice(0, 120)}
                                  {opt.body.trim().length > 120 ? '...' : ''}
                                </p>
                              </div>
                              {extractingOption && selectedOptionIdx === idx && (
                                <Loader2 className="h-4 w-4 animate-spin text-teal-400 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prominent "Save All to Workspace" action bar */}
                  <div className="mt-4 flex items-center gap-2 justify-between border-t border-border/50 pt-4" data-testid="use-this-bar">
                    <Button
                      onClick={handleSave}
                      className={`flex-1 h-10 ${isSaved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-teal-700 hover:bg-primary/90'} text-white`}
                      data-testid="use-this-btn"
                    >
                      {isSaved ? (
                        <><CheckCircle2 className="mr-2 h-4 w-4" />Saved to Workspace</>
                      ) : (
                        <><Bookmark className="mr-2 h-4 w-4" />Save All to Workspace</>
                      )}
                    </Button>
                    <Button
                      onClick={handleGenerate}
                      variant="outline"
                      size="sm"
                      disabled={generating}
                      className="h-10 px-4 flex-shrink-0"
                      data-testid="regenerate-inline-btn"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${generating ? 'animate-spin' : ''}`} />
                      Redo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom action — Complete or Next Step Preview */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {isCompleted && nextStep ? (
            /* Up Next anticipation card */
            (() => {
              const nextMeta = getStepMeta(stepNum + 1);
              const nextPhase = getPhase(stepNum + 1);
              const NextIcon = nextMeta.icon;
              const tagline = getMotivationalTagline(stepNum + 1, steps.length);
              return (
                <div
                  className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-4 border border-slate-200"
                  data-testid="next-step-preview"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${nextMeta.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}
                    >
                      <NextIcon className="text-white" size={22} strokeWidth={2.2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[10px] uppercase tracking-widest mb-0.5 font-bold"
                        style={{ color: nextPhase.color }}
                      >
                        Up Next · {nextPhase.label}
                      </p>
                      <h4 className="font-semibold text-foreground text-base mb-0.5">{nextStep.title}</h4>
                      <p className="text-xs text-muted-foreground line-clamp-2">{nextStep.description}</p>
                      {tagline && (
                        <p className="text-xs text-slate-500 italic mt-1.5">{tagline}</p>
                      )}
                    </div>
                    <Link to={`/path/${pathId}/step/${stepNum + 1}`} className="flex-shrink-0 self-center">
                      <Button
                        className="bg-creataby-gradient-btn text-white hover:opacity-95 creataby-btn-pop px-6 py-3 font-semibold shadow-[0_2px_12px_rgba(14,129,117,0.25)]"
                        data-testid="next-step-btn"
                      >
                        Continue
                        <ArrowRight className="ml-1.5 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })()
          ) : isCompleted && !nextStep ? (
            /* Path complete */
            <div className="flex items-center justify-between gap-4 bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20" data-testid="path-complete-bar">
              <div className="min-w-0">
                <p className="font-medium text-foreground text-sm">Path complete!</p>
                <p className="text-xs text-muted-foreground">You've finished all steps. Nice work.</p>
              </div>
              <Link to="/dashboard">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0" data-testid="finish-btn">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
              </Link>
            </div>
          ) : (
            /* Mark complete */
            <div className="flex items-center justify-between gap-4 bg-secondary/50 rounded-xl p-4 border border-border/50" data-testid="step-action-bar">
              <div className="min-w-0">
                <p className="font-medium text-foreground text-sm">Ready to move on?</p>
                <p className="text-xs text-muted-foreground">Mark this step as done when you're finished.</p>
              </div>
              <Button
                onClick={handleMarkComplete}
                className="bg-creataby-gradient-btn text-white hover:opacity-95 creataby-btn-pop flex-shrink-0 font-semibold shadow-[0_2px_12px_rgba(14,129,117,0.25)]"
                data-testid="complete-step-btn"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Mark Complete</span>
                <span className="sm:hidden">Done</span>
              </Button>
            </div>
          )}
        </motion.div>
      </main>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        subscription={limitSubscription}
      />

      <CelebrationModal
        isOpen={showCelebration}
        onClose={() => { setShowCelebration(false); navigate('/dashboard'); }}
        stepsCompleted={steps.length}
        pathName={steps[0]?.path_id === 'path_fast_cash' ? 'Fast Cash Path' : steps[0]?.path_id === 'path_digital_product' ? 'Digital Product Path' : 'Faceless Affiliate Path'}
      />
    </div>
  );
};

export default StepExecution;
